import React, {useEffect, useRef} from 'react'
import ReactDOM from 'react-dom'
import {useIntl} from 'react-intl'
import {Flex, Image, FlexProps, useMultiStyleConfig} from '@chakra-ui/react'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

interface LoaderProps {
    variant?: 'safari'
}

/**
 * Loader component that displays a loading indicator based on visibility and variant.
 *
 * @param {LoaderProps} props - The properties for the Loader component.
 * @param {string} props.variant - The variant of the loader, used for styling.
 *
 * @returns {JSX.Element | null} The Loader component if visible, otherwise null.
 */
const Loader = ({variant}: LoaderProps) => {
    const {formatMessage} = useIntl()
    const loaderRef = useRef<HTMLElement>()
    const styles = useMultiStyleConfig('Loader', {variant}) as Record<string, FlexProps>

    const statusLabel = formatMessage({id: 'label.loading.inProgress', defaultMessage: 'Loading in progress'})

    useEffect(() => {
        loaderRef.current?.focus()
    }, [])

    // Portal to body so the fixed positioning isn't confined by a transformed ancestor (e.g. a swiper carousel)
    return ReactDOM.createPortal(
        <Flex ref={loaderRef} {...styles.wrapper} data-testid="loader" role="status" tabIndex={-1} aria-label={statusLabel}>
            <Image {...styles.loader} src={getAssetUrl('static/img/bcloader.svg')} alt="Loading logo" aria-hidden />
        </Flex>,
        document.body,
    )
}

export {Loader}
